import React, { useState } from "react";

import { ProgressStepModal, useProgressStepModal } from "src/components/Modal";
import { mapToModalError, useMapToMutationError } from "src/errors";
import { useLazyTestRuleQuery } from "src/graphql/anti-fraud/queries/rules/TestRuleQuery";
import { useT } from "src/internationalization";
import { mapToExecutionResult } from "src/mappers/ruleExecution.mappers";
import { CaseSummary } from "src/types/case";
import { cn } from "src/utils/string.utils";

import { ExecutionParametersForm } from "./ExecutionParametersForm/ExecutionParametersForm";
import {
  useExecutionParametersForm,
  ExecutionParametersFormData,
} from "./ExecutionParametersForm/useExecutionParametersForm";
import { ExecutionResult } from "./ExecutionResult/ExecutionResult";

export type ExecuteRuleModalProps = {
  caseTypeCode: string;
  onClose: () => void;
  selectedCase?: CaseSummary;
};

export const ExecuteRuleModal: React.FC<ExecuteRuleModalProps> = ({
  onClose,
  selectedCase,
  caseTypeCode,
}) => {
  const t = useT();
  const mapToMutationError = useMapToMutationError();
  const [executionResult, setExecutionResult] = useState<ReturnType<
    typeof mapToExecutionResult
  > | null>(null);
  const [executionTime, setExecutionTime] = useState<Date | null>(null);
  const [lastExecutionParams, setLastExecutionParams] = useState<{
    ruleId: string;
    caseId: string;
  } | null>(null);

  const executionParametersFormControl = useExecutionParametersForm(selectedCase);

  const [testRule, { loading, error }] = useLazyTestRuleQuery({
    fetchPolicy: "network-only",
  });

  const submit = async (data: ExecutionParametersFormData) => {
    if (!data.rule || !data.caseItem) return;

    const currentParams = {
      ruleId: data.rule.id,
      caseId: data.caseItem.id,
    };

    // Check if parameters have changed or if we don't have a cached result
    const paramsChanged =
      !lastExecutionParams ||
      lastExecutionParams.ruleId !== currentParams.ruleId ||
      lastExecutionParams.caseId !== currentParams.caseId;

    if (paramsChanged) {
      const { data: result } = await testRule({
        variables: {
          input: currentParams,
        },
      });

      if (result?.testRule) {
        setExecutionResult(mapToExecutionResult(result.testRule));
        setExecutionTime(new Date());
        setLastExecutionParams(currentParams);
        stepsControl.goNextStep();
      }
    } else {
      // Parameters haven't changed, use cached result and go to next step
      stepsControl.goNextStep();
    }
  };

  const { control: stepsControl, modalProps } = useProgressStepModal([
    {
      title: t("page.cases.executeRule.modal.step1.title"),
      type: "params",
      invalid: executionParametersFormControl.hasError,
      disabled: false,
      submit: executionParametersFormControl.submit(submit),
      focus: "#rule-picker",
    },
    {
      title: t("page.cases.executeRule.modal.step2.title"),
      type: "result",
      invalid: false,
      disabled: false,
      focus: undefined,
      submit: onClose,
    },
  ]);

  return (
    <ProgressStepModal
      loading={loading}
      kind="horizontal"
      error={mapToModalError(mapToMutationError(error))}
      title={t("page.cases.executeRule.modal.title")}
      subtitle={t("page.cases.executeRule.modal.subtitle", {
        request: executionParametersFormControl.methods.getValues("caseItem")?.request ?? "",
      })}
      nextButtonText={
        stepsControl.currentStep.type === "params"
          ? t("page.cases.executeRule.modal.step1.execute")
          : undefined
      }
      confirmButtonText={t("general.actions.close")}
      onClose={onClose}
      initialFocus="#rule-picker"
      {...modalProps}
    >
      <div
        className={cn("flex flex-col h-full", {
          hidden: !stepsControl.isStep("params"),
        })}
      >
        <ExecutionParametersForm
          executionParametersFormControl={executionParametersFormControl}
          caseTypeCode={caseTypeCode}
        />
      </div>
      <div
        className={cn("flex flex-col h-full", {
          hidden: !stepsControl.isStep("result"),
        })}
      >
        {executionResult && !loading && (
          <ExecutionResult
            executionTime={executionTime}
            rule={executionResult.rule}
            matched={executionResult.matched}
            relatedCases={executionResult.relatedCases}
            widgetConfig={{ widgetCode: "executionReport" }}
            caseTypeCode={caseTypeCode}
            rowConfiguration={{ widgets: [{ widgetCode: "executionReport" }] }}
          />
        )}
      </div>
    </ProgressStepModal>
  );
};

export default ExecuteRuleModal;
